/**
 * Discount-convention normalization for orders entering WebPOS state.
 *
 * Two conventions exist for `item.discount`:
 *  - Server/online-order convention: the discount is the TOTAL for the line
 *    (unit discount × quantity).
 *  - POS convention: the discount is PER UNIT. Every WebPOS calc helper
 *    (getItemSubtotal, roundOffDiscounts, applyWholeOrderDiscount,
 *    calculateTaxes) assumes per-unit.
 *
 * Feeding a line-style discount into the POS calculators multiplies it by
 * quantity again, inflating discount_total and undercharging the customer.
 * These helpers detect which convention an incoming order uses (by
 * reconciling against the order's own discount_total) and convert line-style
 * discounts to per-unit. The conversion is decision-based and idempotent:
 * an already-per-unit order reconciles and is returned untouched.
 */

const toNumber = (value) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(num) ? num : 0;
};

const isVoided = (item) => Number(item && item.void_status) === 1;

const activeItems = (order) =>
  Array.isArray(order && order.items)
    ? order.items.filter((item) => item && !isVoided(item))
    : [];

// Per-item rounding means each line can drift by up to a cent against the
// stored discount_total, so scale the tolerance with the item count.
const sumTolerance = (itemCount) => Math.max(0.02, itemCount * 0.011);

/**
 * Decide whether the order's item discounts are line totals (server
 * convention) rather than per-unit values (POS convention).
 */
export const shouldTreatDiscountsAsLineTotals = (order) => {
  const items = activeItems(order);
  if (items.length === 0) return false;

  // wholeDiscount only ever exists on orders the POS itself calculated,
  // which are per-unit by construction.
  if (items.some((item) => toNumber(item.wholeDiscount) > 0)) return false;

  const candidates = items.filter(
    (item) =>
      toNumber(item.discount) > 0 && Math.abs(toNumber(item.quantity)) > 1
  );
  if (candidates.length === 0) return false;

  const lineSum = items.reduce((sum, item) => sum + toNumber(item.discount), 0);
  const unitSum = items.reduce(
    (sum, item) =>
      sum + toNumber(item.discount) * (Math.abs(toNumber(item.quantity)) || 1),
    0
  );

  // If both readings produce the same total, there is nothing to fix.
  if (Math.abs(unitSum - lineSum) <= 0.01) return false;

  const discountTotal = toNumber(order.discount_total);
  const tolerance = sumTolerance(items.length);
  const matchesUnit = discountTotal > 0 && Math.abs(unitSum - discountTotal) <= tolerance;
  const matchesLine = discountTotal > 0 && Math.abs(lineSum - discountTotal) <= tolerance;

  if (matchesUnit) return false;
  if (matchesLine) return true;

  // discount_total does not reconcile either way (missing, or polluted by a
  // previous corruption). Fall back to the impossibility test: a per-unit
  // discount can never exceed the unit price, but a line total can.
  return candidates.some(
    (item) =>
      toNumber(item.price) > 0 && toNumber(item.discount) > toNumber(item.price)
  );
};

/**
 * Return the order with item discounts converted from line totals to
 * per-unit values when (and only when) the order is detected to use the
 * server line-total convention. Returns the SAME reference when nothing
 * needed to change, so callers can cheaply detect whether a fix happened.
 */
export const normalizeOrderDiscounts = (order) => {
  if (!order || !Array.isArray(order.items) || order.items.length === 0) {
    return order;
  }
  if (!shouldTreatDiscountsAsLineTotals(order)) return order;

  let changed = false;
  const items = order.items.map((item) => {
    if (!item || isVoided(item)) return item;
    const qty = Math.abs(toNumber(item.quantity));
    const discount = toNumber(item.discount);
    if (qty > 1 && discount > 0) {
      changed = true;
      return { ...item, discount: parseFloat((discount / qty).toFixed(4)) };
    }
    return item;
  });

  return changed ? { ...order, items } : order;
};

/**
 * Reconcile order.discount_total against its own items (POS convention:
 * per-unit discounts). Returns null when consistent, otherwise
 * { expected, actual } describing the mismatch. Used as a pre-payment
 * sanity check so a corrupted total is never charged.
 */
export const getOrderDiscountMismatch = (order) => {
  if (!order || !Array.isArray(order.items)) return null;
  const items = activeItems(order);
  const expected = items.reduce(
    (sum, item) =>
      sum +
      (toNumber(item.discount) + toNumber(item.wholeDiscount)) *
        (Math.abs(toNumber(item.quantity)) || 1),
    0
  );
  const actual = toNumber(order.discount_total);
  if (Math.abs(expected - actual) <= sumTolerance(items.length)) return null;
  return {
    expected: Math.round(expected * 100) / 100,
    actual: Math.round(actual * 100) / 100
  };
};
